import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge, toneOf } from "./ui/badge";
import { cn } from "./ui/utils";
import {
  erpTones,
  formatDateTime,
  formatHours,
  formatMinutes,
  formatNumber,
  humaniseCode,
  minutesSince,
  systemTones,
} from "./d2cFormat";

const TONE_LABEL = { ok: "Healthy", warn: "Worth a look", bad: "Needs attention" };

const StatusPill = ({ tone }) => <Badge tone={toneOf(tone)}>{TONE_LABEL[tone]}</Badge>;

const ROW_TONE = {
  ok: "",
  warn: "tw-bg-amber-50 tw-text-amber-900",
  bad: "tw-bg-red-50 tw-text-red-800",
};

const Row = ({ label, value, tone = "ok", note, indent }) => (
  <li
    data-tone={tone}
    className={cn(
      "tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-sm",
      ROW_TONE[tone],
      indent && "tw-pl-6 tw-text-xs",
    )}
  >
    <span>{label}</span>
    <span className="tw-text-right tw-font-medium tw-tabular-nums">
      {value}
      {note && <span className="tw-ml-1 tw-text-xs tw-font-normal tw-opacity-75">{note}</span>}
    </span>
  </li>
);

const Panel = ({ title, tone, children }) => (
  <Card>
    <CardHeader>
      <CardTitle>{title}</CardTitle>
      <StatusPill tone={tone} />
    </CardHeader>
    <CardContent>
      <ul className="tw-m-0 tw-list-none tw-space-y-0.5 tw-p-0">{children}</ul>
    </CardContent>
  </Card>
);

export const ErpPanel = ({ erp }) => {
  const e = erp || {};
  const tones = erpTones(e);
  return (
    <Panel title="ERP close-out" tone={tones.overall}>
      <Row label="Complete" value={formatNumber(e.complete)} />
      <Row label="In progress" value={formatNumber(e.in_progress)} />
      <Row label="Failed" value={formatNumber(e.failed)} tone={tones.failed} />
      <Row label="Not started" value={formatNumber(e.not_started)} tone={tones.notStarted} />
      <Row label="Median delivery to invoice" value={formatHours(e.median_hours_delivery_to_invoice)} />
    </Panel>
  );
};

export const SystemPanel = ({ system }) => {
  const s = system || {};
  const tones = systemTones(s);
  const scanAge = minutesSince(s.last_scan_at);
  const failed = Object.entries(s.failed_by_kind || {}).filter(([, count]) => Number(count) > 0);

  return (
    <Panel title="System" tone={tones.overall}>
      <Row
        label="Last routing scan"
        value={s.last_scan_at ? formatDateTime(s.last_scan_at) : "Never"}
        note={scanAge === null ? null : `${formatMinutes(scanAge)} ago`}
        tone={tones.lastScan}
      />
      <Row label="Outbox pending" value={formatNumber(s.outbox_pending)} />
      <Row label="Oldest pending" value={formatMinutes(s.oldest_pending_minutes)} tone={tones.oldestPending} />
      <Row label="Held without a routing decision" value={formatNumber(s.held_without_decision)} tone={tones.heldWithoutDecision} />
      <Row
        label="Failed outbox rows"
        value={failed.length === 0 ? "None" : formatNumber(failed.reduce((sum, [, n]) => sum + Number(n), 0))}
        tone={tones.failed}
      />
      {failed.map(([kind, count]) => (
        <Row key={kind} label={humaniseCode(kind)} value={formatNumber(count)} tone="bad" indent />
      ))}
    </Panel>
  );
};
